import * as React from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

type SheetSide = "right" | "left" | "bottom" | "top";

interface SheetProps {
  open: boolean;
  /** Legacy prop — close callback for old-style usage */
  onClose?: () => void;
  /** shadcn/ui-style open-change handler */
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  side?: SheetSide;
  /** Width hint (ignored for side sheets, provided for API compat) */
  width?: string;
}

interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: SheetSide;
  /** Hide the default close button */
  hideClose?: boolean;
  /** Ignored — provided for shadcn/ui API compat */
  onPointerDownOutside?: (e: React.PointerEvent) => void;
  /** Ignored — provided for shadcn/ui API compat */
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PANEL_CLASSES: Record<SheetSide, string> = {
  right:  "absolute inset-y-0 right-0 h-full w-full sm:max-w-md",
  left:   "absolute inset-y-0 left-0 h-full w-full sm:max-w-md",
  bottom: "absolute inset-x-0 bottom-0 w-full rounded-t-xl",
  top:    "absolute inset-x-0 top-0 w-full rounded-b-xl",
};

// ── SheetContent ──────────────────────────────────────────────────────────────

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ side = "right", className, children, hideClose, onPointerDownOutside: _opdo, onEscapeKeyDown: _oekd, ...props }, ref) => {
    const { t } = useTranslation("common");
    const ctx = React.useContext(SheetContext);
    return (
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        className={cn(
          "bg-background flex flex-col shadow-xl",
          PANEL_CLASSES[side],
          className
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {!hideClose && (
          <button
            type="button"
            onClick={ctx?.handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
            aria-label={t("a11y.close")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {children}
      </div>
    );
  }
);
SheetContent.displayName = "SheetContent";

// ── SheetContext ──────────────────────────────────────────────────────────────

interface SheetContextValue {
  handleClose: () => void;
}
const SheetContext = React.createContext<SheetContextValue | null>(null);

// ── Sheet ─────────────────────────────────────────────────────────────────────

function Sheet({ open, onClose, onOpenChange, title, description, children, className, side = "right" }: Readonly<SheetProps>) {
  const { t } = useTranslation("common");

  const handleClose = React.useCallback(() => {
    onClose?.();
    onOpenChange?.(false);
  }, [onClose, onOpenChange]);

  // Lock body scroll when open
  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on Escape key
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, handleClose]);

  if (!open) return null;

  // Detect if children use the new compound API (SheetContent sub-component)
  const hasSheetContent = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && (child as React.ReactElement).type === SheetContent
  );

  return (
    <SheetContext.Provider value={{ handleClose }}>
      <div className="fixed inset-0 z-50">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={handleClose}
          aria-hidden="true"
        />

        {hasSheetContent ? (
          // New API: SheetContent provides its own panel styling
          children
        ) : (
          // Legacy API: Sheet renders the panel directly
          <dialog
            aria-label={title}
            open
            className={cn(
              "absolute bg-background flex flex-col shadow-xl",
              PANEL_CLASSES[side],
              className
            )}
          >
            {(title || description) && (
              <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b shrink-0">
                <div className="space-y-1">
                  {title && <h2 className="text-base font-semibold leading-none">{title}</h2>}
                  {description && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label={t("a11y.close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {children}
          </dialog>
        )}
      </div>
    </SheetContext.Provider>
  );
}

// ── SheetHeader ───────────────────────────────────────────────────────────────

const SheetHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  )
);
SheetHeader.displayName = "SheetHeader";

// ── SheetTitle ────────────────────────────────────────────────────────────────

const SheetTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h2 ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
  )
);
SheetTitle.displayName = "SheetTitle";

// ── SheetDescription ──────────────────────────────────────────────────────────

const SheetDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )
);
SheetDescription.displayName = "SheetDescription";

// ── SheetClose ────────────────────────────────────────────────────────────────

const SheetClose = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, onClick, ...props }, ref) => {
    const ctx = React.useContext(SheetContext);
    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => { ctx?.handleClose(); onClick?.(e); }}
        className={cn("rounded-sm opacity-70 hover:opacity-100", className)}
        {...props}
      />
    );
  }
);
SheetClose.displayName = "SheetClose";

// ── SheetBody ─────────────────────────────────────────────────────────────────

const SheetBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex-1 overflow-y-auto px-6 py-4 space-y-4", className)} {...props} />
  )
);
SheetBody.displayName = "SheetBody";

// ── SheetFooter ───────────────────────────────────────────────────────────────

const SheetFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 py-4 border-t", className)}
      {...props}
    />
  )
);
SheetFooter.displayName = "SheetFooter";

export { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose, SheetBody, SheetFooter };
export type { SheetProps };
